import React, { useState, useEffect } from 'react';
import Button from '../../components/ui/Button';
import Icon from '../../components/AppIcon';
import Select from '../../components/ui/Select';
import serviceManagementService from '../../services/serviceManagementService';
import { advancedBillingService } from '../../services/advancedBillingService';
import CountryServiceMatrix from './components/CountryServiceMatrix';
import ComplianceTracker from './components/ComplianceTracker';
import RegionalCustomization from './components/RegionalCustomization';

export function CountryServiceConfigContent() {
  const [currentLanguage] = useState(() => localStorage.getItem('language') || 'FR');
  const [countries, setCountries] = useState([]);
  const [selectedCountry, setSelectedCountry] = useState(null);
  const [serviceAvailability, setServiceAvailability] = useState([]);
  const [matrixData, setMatrixData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [activeTab, setActiveTab] = useState('matrix');

  useEffect(() => {
    fetchCountries();
    fetchMatrix();
  }, []);

  useEffect(() => {
    if (selectedCountry) {
      fetchServiceAvailability(selectedCountry?.id);
    }
  }, [selectedCountry]);

  const fetchCountries = async () => {
    try {
      const data = await advancedBillingService?.getAllCountries();
      setCountries(data);
      if (data?.length > 0 && !selectedCountry) {
        setSelectedCountry(data?.[0]);
      }
    } catch (err) {
      console.error('Error fetching countries:', err);
      setCountries([]);
    }
  };

  const fetchServiceAvailability = async (countryId) => {
    try {
      setLoading(true);
      setError(null);
      const data = await serviceManagementService?.getServiceAvailabilityByCountry(countryId);
      setServiceAvailability(data);
    } catch (err) {
      console.error('Error fetching service availability:', err);
      setError(err?.message || 'Erreur lors du chargement');
      setServiceAvailability([]);
    } finally {
      setLoading(false);
    }
  };

  const fetchMatrix = async () => {
    try {
      const data = await serviceManagementService?.getCountryAvailabilityMatrix();
      setMatrixData(data);
    } catch (err) {
      console.error('Error fetching matrix:', err);
      setMatrixData(null);
    }
  };

  const handleCountryChange = (countryId) => {
    const country = countries?.find((c) => c?.id === countryId);
    setSelectedCountry(country);
  };

  const handleRefresh = () => {
    if (selectedCountry) {
      fetchServiceAvailability(selectedCountry?.id);
    }
    fetchMatrix();
  };

  const tabs = [
    { id: 'matrix', label: 'Matrice de disponibilité', icon: 'Grid' },
    { id: 'customization', label: 'Personnalisation régionale', icon: 'Settings' },
    { id: 'compliance', label: 'Suivi de conformité', icon: 'Shield' },
  ];

  const countryOptions = [
    { value: '', label: 'Sélectionner un pays' },
    ...countries?.map((country) => ({
      value: country?.id,
      label: `${country?.countryName} (${country?.countryCode})`,
    })),
  ];

  const renderTabContent = () => {
    switch (activeTab) {
      case 'matrix':
        return (
          <CountryServiceMatrix
            matrixData={matrixData}
            onRefresh={handleRefresh}
            language={currentLanguage}
          />
        );
      case 'customization':
        return (
          <RegionalCustomization
            country={selectedCountry}
            serviceAvailability={serviceAvailability}
            onRefresh={handleRefresh}
            loading={loading}
            language={currentLanguage}
          />
        );
      case 'compliance':
        return (
          <ComplianceTracker
            country={selectedCountry}
            serviceAvailability={serviceAvailability}
            loading={loading}
            language={currentLanguage}
          />
        );
      default:
        return null;
    }
  };

  return (
    <>
      <div className="flex items-center justify-between">
        <div>
          <h2 className="text-2xl font-bold text-foreground mb-2">Configuration pays-services</h2>
          <p className="text-muted-foreground">
            Disponibilité des services par pays et conformité réglementaire
          </p>
        </div>
        <Button onClick={handleRefresh} iconName="RefreshCw" iconPosition="left" variant="outline">
          Actualiser
        </Button>
      </div>

      {error && (
        <div className="bg-destructive/10 border border-destructive/20 rounded-lg p-4 text-destructive text-sm">
          {error}
        </div>
      )}

      {(activeTab === 'customization' || activeTab === 'compliance') && (
        <div className="bg-card border border-border rounded-lg p-4">
          <Select
            label="Pays sélectionné"
            value={selectedCountry?.id || ''}
            onChange={(e) => handleCountryChange(e?.target?.value)}
            options={countryOptions}
          />
        </div>
      )}

      <div className="bg-card border border-border rounded-lg overflow-hidden">
        <div className="flex flex-wrap border-b border-border">
          {tabs?.map((tab) => (
            <button
              key={tab?.id}
              type="button"
              onClick={() => setActiveTab(tab?.id)}
              className={`flex items-center gap-2 px-4 py-3 text-sm font-medium transition-colors ${
                activeTab === tab?.id
                  ? 'bg-primary text-primary-foreground border-b-2 border-primary'
                  : 'text-muted-foreground hover:bg-muted/50'
              }`}
            >
              <Icon name={tab?.icon} size={18} />
              {tab?.label}
            </button>
          ))}
        </div>
        <div className="p-4 md:p-6">{renderTabContent()}</div>
      </div>
    </>
  );
}
